import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { classifyCsv, parseYearMonth, sortRowsByMonth, CHANNEL_NAME_KEY } from "../utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIOD_LABELS = { current: "Report month", previous: "Previous month", lastYear: "Last year (same month)" };

export default function StepUpload({ files, setFiles, kpiRows, config, setConfig, goTo }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const nextFreePeriod = (existing) => {
    const used = existing.filter((f) => f.type === "channel").map((f) => f.period);
    return ["current", "previous", "lastYear"].find((p) => !used.includes(p)) || "current";
  };

  const handleFiles = async (fileList) => {
    const list = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".csv"));
    if (list.length === 0) { toast.error("Please drop .csv files only"); return; }
    setBusy(true);
    let added = [...files];
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/parse-csv`, fd);
        if (!res.data.success || res.data.data.length === 0) {
          added = [...added, { id: crypto.randomUUID(), filename: file.name, type: "error", error: res.data.error || "File is empty or unreadable" }];
          continue;
        }
        const rows = res.data.data;
        const headers = res.data.headers;
        const type = classifyCsv(headers);
        if (type === "unknown") {
          added = [...added, { id: crypto.randomUUID(), filename: file.name, type: "error", error: "Could not recognize this file. Expected a 'Year month' column (KPI data) or a 'Session default channel group' column (channel data)." }];
          continue;
        }
        const entry = { id: crypto.randomUUID(), filename: file.name, type, rows, headers };
        if (type === "kpi" || type === "organic") {
          entry.months = Array.from(new Set(rows.map((r) => r["Year month"]).filter(Boolean)));
          entry.hasChannelCol = headers.some((h) => h.toLowerCase().includes("channel group"));
        } else {
          entry.channelCount = rows.filter((r) => r[CHANNEL_NAME_KEY]).length;
          entry.period = nextFreePeriod(added);
        }
        added = [...added, entry];
      } catch (e) {
        added = [...added, { id: crypto.randomUUID(), filename: file.name, type: "error", error: "Upload failed — check the file and try again" }];
      }
    }
    setFiles(added);
    // Auto-select the latest month found in KPI data
    const allMonths = added.filter((f) => f.type === "kpi").flatMap((f) => f.months || []);
    if (allMonths.length > 0) {
      const sorted = sortRowsByMonth(allMonths.map((m) => ({ "Year month": m })));
      const latest = parseYearMonth(sorted[sorted.length - 1]["Year month"]);
      if (latest) setConfig((prev) => ({ ...prev, month: latest.month, year: latest.year }));
    }
    setBusy(false);
    const ok = added.filter((f) => f.type !== "error").length;
    if (ok > 0) toast.success(`${ok} file(s) ready`);
  };

  const removeFile = (id) => setFiles(files.filter((f) => f.id !== id));
  const setPeriod = (id, period) =>
    setFiles(files.map((f) => (f.id === id ? { ...f, period } : f)));
  const setType = (id, type) =>
    setFiles(files.map((f) => (f.id === id ? { ...f, type } : f)));

  const TYPE_STYLE = {
    kpi: { badge: "KPI", cls: "bg-[#E6F4EA] text-[#137333]", icon: "#188038" },
    channel: { badge: "CHANNELS", cls: "bg-[#E8F0FE] text-[#1A73E8]", icon: "#1A73E8" },
    organic: { badge: "ORGANIC", cls: "bg-[#FEF7E0] text-[#B06000]", icon: "#E37400" },
  };

  const hasKpi = kpiRows.length > 0;
  const validFiles = files.filter((f) => f.type !== "error");
  const errorFiles = files.filter((f) => f.type === "error");

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Drop in your exports</h1>
      <p className="text-center text-gray-500 mt-3 mb-8 text-sm sm:text-base">
        KPI history, channel and organic-history CSVs — we'll recognize each file automatically. No mapping, no setup.
      </p>

      <div
        className={`v1-dropzone p-12 text-center cursor-pointer ${drag ? "drag" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        data-testid="v1-upload-dropzone"
      >
        <Upload className="w-10 h-10 mx-auto mb-4" style={{ color: "#188038" }} />
        <div className="font-bold text-lg">{busy ? "Reading files…" : "Drag & drop CSV files here"}</div>
        <div className="text-sm text-gray-500 mt-1">or click to browse — you can drop several at once</div>
        <input ref={inputRef} type="file" accept=".csv" multiple className="hidden" data-testid="v1-upload-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {validFiles.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="v1-file-list">
          {validFiles.map((f) => (
            <div key={f.id} className="v1-card p-4 flex items-center gap-4" data-testid={`v1-file-card-${f.type}`}>
              <FileSpreadsheet className="w-8 h-8 flex-shrink-0" style={{ color: TYPE_STYLE[f.type].icon }} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{f.filename}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {f.type === "channel"
                    ? `Channel data — ${f.channelCount} channels`
                    : f.type === "organic"
                    ? `Organic history — ${f.months.length} month${f.months.length > 1 ? "s" : ""}${f.hasChannelCol ? " (Organic Search rows auto-extracted)" : ""}`
                    : `KPI history — ${f.months.length} month${f.months.length > 1 ? "s" : ""} detected (${f.months.slice(0, 4).join(", ")}${f.months.length > 4 ? "…" : ""})`}
                </div>
              </div>
              {f.type === "channel" && (
                <select
                  value={f.period}
                  onChange={(e) => setPeriod(f.id, e.target.value)}
                  className="text-xs border border-gray-300 rounded-full px-3 py-1.5 bg-white font-semibold"
                  data-testid="v1-channel-period-select"
                >
                  {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              )}
              {(f.type === "kpi" || (f.type === "organic" && !f.hasChannelCol)) && (
                <select
                  value={f.type}
                  onChange={(e) => setType(f.id, e.target.value)}
                  className="text-xs border border-gray-300 rounded-full px-3 py-1.5 bg-white font-semibold"
                  data-testid="v1-file-type-select"
                >
                  <option value="kpi">KPI history</option>
                  <option value="organic">Organic history</option>
                </select>
              )}
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${TYPE_STYLE[f.type].cls}`}>
                {TYPE_STYLE[f.type].badge}
              </span>
              <button onClick={() => removeFile(f.id)} className="text-gray-400 hover:text-red-600" data-testid="v1-remove-file-button">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {errorFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {errorFiles.map((f) => (
            <div key={f.id} className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50" data-testid="v1-file-error">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm text-red-800">{f.filename}</div>
                <div className="text-xs text-red-700 mt-0.5">{f.error}</div>
              </div>
              <button onClick={() => removeFile(f.id)} className="text-red-400 hover:text-red-700"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          {hasKpi && <><CheckCircle2 className="w-4 h-4 text-[#137333]" /> {kpiRows.length} months of KPI history merged</>}
        </div>
        <button onClick={() => goTo(1)} disabled={!hasKpi} className="v1-btn-primary" data-testid="v1-upload-continue-button">
          Continue →
        </button>
      </div>
    </div>
  );
}
